import { FileLikeObject, FileUploaderOptions, ParsedResponseHeaders } from './file-upload.types';

export class FileItem {
  public file: FileLikeObject;
  public _file: File;
  public uploader: FileUploader;
  public withCredentials: boolean = true;
  public formData: any = [];
  public isReady: boolean = false;
  public isUploading: boolean = false;
  public isUploaded: boolean = false;
  public isSuccess: boolean = false;
  public isCancel: boolean = false;
  public isError: boolean = false;
  public progress: number = 0;
  public index: number;
  public alias: string;
  public url: string;
  public method: string;
  public headers: { name: string; value: string }[];
  public _xhr: XMLHttpRequest;
  public _form: FormData;
  public someFlagForOptions?: any;

  constructor(uploader: FileUploader, file: File, options?: any) {
    this.uploader = uploader;
    this._file = file;
    this.file = {
      name: file.name,
      size: file.size,
      type: file.type,
      rawFile: file,
    };
    this.alias = options?.alias ?? uploader.options.itemAlias ?? 'file';
    this.url = options?.url ?? uploader.options.url ?? '';
    this.method = options?.method ?? uploader.options.method ?? 'POST';
    this.headers = options?.headers ?? (uploader.options.headers ? [...uploader.options.headers] : []);
    this.withCredentials = options?.withCredentials ?? uploader.options.withCredentials ?? false;
  }

  public upload(): void {
    this.uploader.uploadItem(this);
  }

  public cancel(): void {
    this.uploader.cancelItem(this);
  }

  public remove(): void {
    this.uploader.removeFromQueue(this);
  }

  public onBeforeUpload(): void {}
  public onBuildForm(form: any): any { return form; }
  public onProgress(progress: number): any { return progress; }
  public onSuccess(_response: string, _status: number, _headers: ParsedResponseHeaders): any { return { _response, _status, _headers }; }
  public onError(_response: string, _status: number, _headers: ParsedResponseHeaders): any { return { _response, _status, _headers }; }
  public onCancel(_response: string, _status: number, _headers: ParsedResponseHeaders): any { return { _response, _status, _headers }; }
  public onComplete(_response: string, _status: number, _headers: ParsedResponseHeaders): any { return { _response, _status, _headers }; }

  public _onBeforeUpload(): void {
    this.isReady = true;
    this.isUploading = true;
    this.isUploaded = false;
    this.isSuccess = false;
    this.isCancel = false;
    this.isError = false;
    this.progress = 0;
    this.onBeforeUpload();
  }

  public _onBuildForm(form: any): void {
    this.onBuildForm(form);
  }

  public _onProgress(progress: number): void {
    this.progress = progress;
    this.onProgress(progress);
  }

  public _onSuccess(response: string, status: number, headers: ParsedResponseHeaders): void {
    this.isReady = false;
    this.isUploading = false;
    this.isUploaded = true;
    this.isSuccess = true;
    this.isCancel = false;
    this.isError = false;
    this.progress = 100;
    this.onSuccess(response, status, headers);
  }

  public _onError(response: string, status: number, headers: ParsedResponseHeaders): void {
    this.isReady = false;
    this.isUploading = false;
    this.isUploaded = true;
    this.isSuccess = false;
    this.isCancel = false;
    this.isError = true;
    this.progress = 0;
    this.onError(response, status, headers);
  }

  public _onCancel(response: string, status: number, headers: ParsedResponseHeaders): void {
    this.isReady = false;
    this.isUploading = false;
    this.isUploaded = false;
    this.isSuccess = false;
    this.isCancel = true;
    this.isError = false;
    this.progress = 0;
    this.onCancel(response, status, headers);
  }

  public _onComplete(response: string, status: number, headers: ParsedResponseHeaders): void {
    this.onComplete(response, status, headers);
  }

  public _prepareToUploading(): void {
    this.index = this.index || ++this.uploader._nextIndex;
    this.isReady = true;
  }
}

const DEFAULT_OPTIONS: FileUploaderOptions = {
  autoUpload: false,
  isHTML5: true,
  removeAfterUpload: false,
  disableMultipart: false,
  method: 'POST',
  authTokenHeader: 'Authorization',
  withCredentials: false,
  parametersBeforeFiles: false,
};

export class FileUploader {
  public options: FileUploaderOptions;
  public authToken: string;
  public authTokenHeader: string;
  public queue: FileItem[] = [];
  public progress: number = 0;
  public isUploading: boolean = false;
  public _nextIndex: number = 0;
  public autoUpload: boolean;

  constructor(options: FileUploaderOptions) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.authToken = options.authToken;
    this.authTokenHeader = this.options.authTokenHeader || 'Authorization';
    this.autoUpload = !!this.options.autoUpload;
  }

  // Hooks consumers override.
  public onAfterAddingAll(_fileItems: any): void {}
  public onAfterAddingFile(_fileItem: FileItem): void {}
  public onWhenAddingFileFailed(_item: FileLikeObject, _filter: any, _options: any): void {}
  public onBeforeUploadItem(_fileItem: FileItem): void {}
  public onBuildItemForm(_fileItem: FileItem, _form: any): void {}
  public onProgressItem(_fileItem: FileItem, _progress: any): void {}
  public onProgressAll(_progress: any): void {}
  public onSuccessItem(_item: FileItem, _response: string, _status: number, _headers: ParsedResponseHeaders): void {}
  public onErrorItem(_item: FileItem, _response: string, _status: number, _headers: ParsedResponseHeaders): void {}
  public onCancelItem(_item: FileItem, _response: string, _status: number, _headers: ParsedResponseHeaders): void {}
  public onCompleteItem(_item: FileItem, _response: string, _status: number, _headers: ParsedResponseHeaders): void {}
  public onCompleteAll(): void {}

  public setOptions(options: FileUploaderOptions): void {
    this.options = { ...this.options, ...options };
    this.authToken = options.authToken !== undefined ? options.authToken : this.authToken;
    this.authTokenHeader = this.options.authTokenHeader || this.authTokenHeader || 'Authorization';
    this.autoUpload = !!this.options.autoUpload;

    if (this.options.queueLimit !== undefined) {
      while (this.queue.length > this.options.queueLimit) {
        this.queue.pop();
      }
    }

    if (this.options.autoUpload) {
      this.uploadAll();
    }
  }

  public addToQueue(files: File[] | FileList, options?: any, _filters?: any): void {
    const list: File[] = Array.isArray(files) ? files : Array.from(files);
    const accepted: FileItem[] = [];
    list.forEach((file) => {
      if (!this._isValidFile(file)) {
        this.onWhenAddingFileFailed({ name: file.name, size: file.size, type: file.type, rawFile: file }, null, options);
        return;
      }
      const fileItem = new FileItem(this, file, options);
      this.queue.push(fileItem);
      accepted.push(fileItem);
      this.onAfterAddingFile(fileItem);
    });

    if (accepted.length) {
      this.onAfterAddingAll(accepted);
      this.progress = this._getTotalProgress();
      if (this.autoUpload) {
        this.uploadAll();
      }
    }
  }

  public removeFromQueue(item: FileItem): void {
    const index = this.queue.indexOf(item);
    if (index === -1) { return; }
    if (item.isUploading) {
      item.cancel();
    }
    this.queue.splice(index, 1);
    this.progress = this._getTotalProgress();
  }

  public clearQueue(): void {
    while (this.queue.length) {
      this.queue[0].remove();
    }
    this.progress = 0;
  }

  public uploadAll(): void {
    const items = this.getNotUploadedItems().filter((item) => !item.isUploading);
    if (!items.length) { return; }
    items.forEach((item) => item._prepareToUploading());
    this.isUploading = true;
    items[0].upload();
  }

  public cancelAll(): void {
    this.getNotUploadedItems().forEach((item) => item.cancel());
  }

  public uploadItem(value: FileItem): void {
    const index = this.getIndexOfItem(value);
    const item = this.queue[index];
    item._prepareToUploading();
    if (this.options.isHTML5 === false) {
      console.warn('Non-HTML5 uploads are not supported by the in-tree FileUploader replacement.');
      return;
    }
    this._xhrTransport(item);
  }

  public cancelItem(value: FileItem): void {
    const index = this.getIndexOfItem(value);
    const item = this.queue[index];
    if (item && item._xhr) {
      item._xhr.abort();
    }
  }

  public getNotUploadedItems(): FileItem[] {
    return this.queue.filter((item) => !item.isUploaded);
  }

  public getReadyItems(): FileItem[] {
    return this.queue
      .filter((item) => item.isReady && !item.isUploading)
      .sort((a, b) => a.index - b.index);
  }

  public getIndexOfItem(value: FileItem | number): number {
    return typeof value === 'number' ? value : this.queue.indexOf(value);
  }

  public destroy(): void {
    /* no-op */
  }

  public isFile(value: any): boolean {
    return File && value instanceof File;
  }

  public isFileLikeObject(value: any): boolean {
    return value && typeof value.size === 'number' && typeof value.name === 'string';
  }

  protected _isValidFile(file: File): boolean {
    if (this.options.queueLimit !== undefined && this.queue.length >= this.options.queueLimit) {
      return false;
    }
    if (this.options.allowedMimeType && this.options.allowedMimeType.length) {
      if (!this.options.allowedMimeType.includes(file.type)) {
        return false;
      }
    }
    if (this.options.allowedFileType && this.options.allowedFileType.length) {
      const category = this._mimeCategory(file.type);
      if (!this.options.allowedFileType.includes(category)) {
        return false;
      }
    }
    if (this.options.maxFileSize !== undefined && file.size > this.options.maxFileSize) {
      return false;
    }
    return true;
  }

  protected _mimeCategory(mime: string): string {
    if (!mime) { return 'application'; }
    if (mime.startsWith('image/')) { return 'image'; }
    if (mime.startsWith('audio/')) { return 'audio'; }
    if (mime.startsWith('video/')) { return 'video'; }
    if (mime.includes('pdf')) { return 'pdf'; }
    if (mime.includes('compressed') || mime.includes('zip')) { return 'compress'; }
    if (mime.includes('msword') || mime.includes('text/plain') || mime.includes('officedocument')) { return 'doc'; }
    if (mime.includes('xls') || mime.includes('csv') || mime.includes('spreadsheet')) { return 'xls'; }
    return 'application';
  }

  protected _getTotalProgress(value: number = 0): number {
    if (this.options.removeAfterUpload) {
      return value;
    }
    const notUploaded = this.getNotUploadedItems().length;
    const uploaded = notUploaded ? this.queue.length - notUploaded : this.queue.length;
    const ratio = 100 / this.queue.length;
    const current = (value * ratio) / 100;
    return Math.round(uploaded * ratio + current);
  }

  protected _parseHeaders(headers: string): ParsedResponseHeaders {
    const parsed: ParsedResponseHeaders = {};
    if (!headers) { return parsed; }
    headers.split('\n').forEach((line) => {
      const idx = line.indexOf(':');
      if (idx <= 0) { return; }
      const key = line.slice(0, idx).trim().toLowerCase();
      const value = line.slice(idx + 1).trim();
      if (key) {
        parsed[key] = parsed[key] ? `${parsed[key]}, ${value}` : value;
      }
    });
    return parsed;
  }

  protected _xhrTransport(item: FileItem): void {
    const xhr = (item._xhr = new XMLHttpRequest());

    let body: Document | XMLHttpRequestBodyInit | null;
    if (this.options.disableMultipart) {
      body = item._file;
    } else {
      const form = new FormData();
      const appendFile = () => form.append(item.alias, item._file, item.file.name);
      const appendParams = () => {
        if (this.options.additionalParameter) {
          Object.keys(this.options.additionalParameter).forEach((key) => {
            const value = this.options.additionalParameter[key];
            // ng2-file-upload supports {{file_name}} substitution.
            if (typeof value === 'string' && value.includes('{{file_name}}')) {
              form.append(key, value.replace('{{file_name}}', item.file.name));
            } else {
              form.append(key, value);
            }
          });
        }
      };
      if (this.options.parametersBeforeFiles) {
        appendParams();
        appendFile();
      } else {
        appendFile();
        appendParams();
      }
      this.onBuildItemForm(item, form);
      item._form = form;
      body = form;
    }

    item._onBeforeUpload();
    this.onBeforeUploadItem(item);

    if (xhr.upload) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        const progress = event.lengthComputable ? Math.round((event.loaded * 100) / event.total) : 0;
        item._onProgress(progress);
        this.onProgressItem(item, progress);
        const totalProgress = this._getTotalProgress(progress);
        this.progress = totalProgress;
        this.onProgressAll(totalProgress);
      };
    }

    xhr.onload = () => {
      const headers = this._parseHeaders(xhr.getAllResponseHeaders());
      const response = xhr.response;
      const ok = (xhr.status >= 200 && xhr.status < 300) || xhr.status === 304;
      const cb = ok ? '_onSuccess' : '_onError';
      const upperCb = ok ? 'onSuccessItem' : 'onErrorItem';
      item[cb](response, xhr.status, headers);
      (this as any)[upperCb](item, response, xhr.status, headers);
      this._onCompleteItem(item, response, xhr.status, headers);
    };

    xhr.onerror = () => {
      const headers = this._parseHeaders(xhr.getAllResponseHeaders());
      const response = xhr.response;
      item._onError(response, xhr.status, headers);
      this.onErrorItem(item, response, xhr.status, headers);
      this._onCompleteItem(item, response, xhr.status, headers);
    };

    xhr.onabort = () => {
      const headers = this._parseHeaders(xhr.getAllResponseHeaders());
      const response = xhr.response;
      item._onCancel(response, xhr.status, headers);
      this.onCancelItem(item, response, xhr.status, headers);
      this._onCompleteItem(item, response, xhr.status, headers);
    };

    const url = item.url || this.options.url || '';
    const method = (item.method || this.options.method || 'POST').toUpperCase();
    xhr.open(method, url, true);
    xhr.withCredentials = item.withCredentials;

    const headers = item.headers && item.headers.length ? item.headers : (this.options.headers || []);
    headers.forEach(({ name, value }) => xhr.setRequestHeader(name, value));
    if (this.authToken) {
      xhr.setRequestHeader(this.authTokenHeader, this.authToken);
    }

    xhr.send(body as any);
  }

  protected _onCompleteItem(item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): void {
    item._onComplete(response, status, headers);
    this.onCompleteItem(item, response, status, headers);

    const nextItem = this.getReadyItems()[0];
    this.isUploading = false;
    if (nextItem) {
      nextItem.upload();
      return;
    }
    this.onCompleteAll();
    this.progress = this._getTotalProgress();
  }
}
